import { Button, Dimmer, Form, Loader, Modal } from '@mairistem/composants';
import {
	AnalytiqueSearchInput,
	ArticleSearchInput,
	ChapitreSearchInput,
	FonctionSearchInput,
	type Imputation,
	OperationSearchInput,
	VentilationSearchInput,
} from '@mairistem/comptabilite';
import { Combobox, getErrorMessage } from '@mairistem/finances-utils';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type Simulation from '../../../../interfaces/simulation/simulation';
import { createImputation } from '../../../../services/simulation/imputation';
import {
	type CreateImputation,
	checkImputation,
} from '../../../../utils/imputation';
import { getRowFilters } from '../../../../utils/treeView';
import type { OutilModalData } from '../../ImputationTable';

const optionSection = [
	{
		key: 'F',
		text: 'Fonctionnement',
		value: 'F',
	},
	{
		key: 'I',
		text: 'Investissement',
		value: 'I',
	},
];

const optionSens = [
	{
		key: 'D',
		text: 'Dépense',
		value: 'D',
	},
	{
		key: 'R',
		text: 'Recette',
		value: 'R',
	},
];

interface CreationImputationModalProps {
	data: OutilModalData;
	onClose: () => void;
	onValidate: (imputation: Imputation) => void;
	open: boolean;
	simulation: Simulation;
}

const CreationImputationModal = ({
	data,
	onClose,
	onValidate,
	open,
	simulation,
}: CreationImputationModalProps) => {
	const [modalLoading, setModalLoading] = React.useState<boolean>(true);
	const [imputationData, setImputationData] =
		React.useState<CreateImputation>();

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'data'
	React.useEffect(() => {
		setModalLoading(false);
		setImputationData({
			section: data?.feuilleSaisie?.section,
			...getRowFilters(data.rowData, data?.filters, data?.feuilleSaisie, true),
		});
	}, []);

	const handleChangeImputation = (name, item, libelle) => {
		setImputationData((old) => ({
			...old,
			[name]: item?.code,
			[libelle]: item?.libelle,
		}));
	};

	const handleChangeImputationChapitre = (name, item) => {
		setImputationData((old) => ({
			...old,
			[name]: item?.code,
			cha_libelle: item?.libelle,
			codeArticle: null,
			art_libelle: null,
		}));
	};

	const handleCreateImputation = async () => {
		try {
			checkImputation(imputationData);

			try {
				const imputatiton = await createImputation(
					simulation?.identifiant,
					imputationData,
				);
				onValidate?.(imputatiton);
			} catch (e) {
				toast.error(
					getErrorMessage(e, "Erreur lors de la création de l'imputation"),
				);
			}
		} catch (e) {
			toast.error(e.message);
		}
	};

	const handleDropdownChange = (_e, { name, value }) => {
		setImputationData((old) => ({
			...old,
			[name]: value,
		}));
	};

	const handleSectionChange = (e, { name, value }) => {
		setImputationData((old) => ({
			...old,
			codeArticle: null,
			art_libelle: null,
			codeChapitre: null,
			cha_libelle: null,
		}));
		handleDropdownChange(e, { name, value });
	};

	return (
		<Modal closeIcon onClose={onClose} open={open}>
			<Modal.Header content={"Création d'imputation"} />
			<Modal.Content>
				{modalLoading ? (
					<Dimmer active inverted>
						<Loader size="massive" />
					</Dimmer>
				) : (
					<Form>
						<Form.Field
							control={Combobox}
							label="Sens"
							name="sens"
							onChange={handleDropdownChange}
							options={optionSens}
							required
							selection
							value={imputationData?.sens}
						/>

						<Form.Field
							control={Combobox}
							label="Section"
							name="section"
							onChange={handleSectionChange}
							options={optionSection}
							required
							selection
							value={imputationData?.section}
						/>

						<Form.Field
							control={ChapitreSearchInput}
							identifiantExercice={simulation.identifiantExercice}
							menuPortalTarget={document.body}
							section={imputationData?.section}
							value={
								isNil(imputationData?.codeChapitre)
									? ''
									: `${imputationData?.codeChapitre} - ${imputationData?.cha_libelle}`
							}
							placeholder="Chapitre"
							name="codeChapitre"
							label="Chapitre"
							onSelect={(name, item) =>
								handleChangeImputationChapitre(name, item)
							}
							required
						/>

						<Form.Field
							control={ArticleSearchInput}
							value={
								isNil(imputationData?.codeArticle)
									? ''
									: `${imputationData?.codeArticle} - ${imputationData?.art_libelle} - (${imputationData?.codeChapitre})`
							}
							identifiantExercice={simulation.identifiantExercice}
							menuPortalTarget={document.body}
							section={imputationData?.section}
							placeholder="Article"
							name="codeArticle"
							label="Article"
							sens={imputationData?.sens}
							codeChapitre={imputationData?.codeChapitre}
							onSelect={(name, item) =>
								handleChangeImputation(name, item, 'art_libelle')
							}
							required
						/>

						{imputationData?.section !== 'F' && (
							<Form.Field
								control={OperationSearchInput}
								value={
									!isNil(imputationData?.codeOperation)
										? `${imputationData?.codeOperation} - ${imputationData?.ope_libelle}`
										: ''
								}
								identifiantEntite={simulation.exercice?.identifiantEntite}
								menuPortalTarget={document.body}
								section={imputationData?.section}
								placeholder="Opération"
								name="codeOperation"
								label="Opération"
								codeChapitre={imputationData?.codeOperation}
								onSelect={(name, item) =>
									handleChangeImputation(name, item, 'ope_libelle')
								}
								required
							/>
						)}

						{simulation?.exercice?.gestionFonctionnelle && (
							<Form.Field
								control={FonctionSearchInput}
								value={
									!isNil(imputationData?.codeFonction)
										? `${imputationData?.codeFonction} - ${imputationData?.fon_libelle}`
										: ''
								}
								identifiantExercice={simulation.identifiantExercice}
								menuPortalTarget={document.body}
								section={imputationData?.section}
								placeholder="Fonction"
								name="codeFonction"
								label="Fonction"
								codeChapitre={imputationData?.codeFonction}
								onSelect={(name, item) =>
									handleChangeImputation(name, item, 'fon_libelle')
								}
							/>
						)}

						{simulation?.exercice?.gestionAnalytique &&
							!simulation?.exercice.anaExec && (
								<Form.Field
									control={AnalytiqueSearchInput}
									value={
										!isNil(imputationData?.codeAnalytique)
											? `${imputationData?.codeAnalytique} - ${imputationData?.ana_libelle}`
											: ''
									}
									identifiantExercice={simulation.identifiantExercice}
									menuPortalTarget={document.body}
									section={imputationData?.section}
									placeholder="Analytique"
									name="codeAnalytique"
									label="Analytique"
									codeChapitre={imputationData?.codeAnalytique}
									onSelect={(name, item) =>
										handleChangeImputation(name, item, 'ana_libelle')
									}
								/>
							)}

						{simulation?.exercice?.gestionVentilation &&
							!simulation?.exercice.venExec && (
								<Form.Field
									control={VentilationSearchInput}
									value={
										!isNil(imputationData?.codeVentilation)
											? `${imputationData?.codeVentilation} - ${imputationData?.ven_libelle}`
											: ''
									}
									identifiantExercice={simulation.identifiantExercice}
									menuPortalTarget={document.body}
									section={imputationData?.section}
									placeholder="Ventilation"
									name="codeVentilation"
									label="Ventilation"
									codeChapitre={imputationData?.codeVentilation}
									onSelect={(name, item) =>
										handleChangeImputation(name, item, 'ven_libelle')
									}
								/>
							)}
					</Form>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Annuler"
					data-testid="closeButton"
					icon="x"
					onClick={onClose}
					size="tiny"
				/>
				<Button
					content="Créer l'imputation"
					data-testid="confirmButton"
					onClick={handleCreateImputation}
					positive
				/>
			</Modal.Actions>
		</Modal>
	);
};

export default CreationImputationModal;
